import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { DateField, FlexColumn, FormModal, InputField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { getDirtyFields, isValueBetweenNumberRange } from '@ubeya/shared/utils/form';
import moment from 'moment';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 16px 28px;
`;

const formatDate = (value) => (value ? moment(value).format(API_DATE_FORMAT) : undefined);

// BE rejects future startDate; forward moves are blocked by start_date_cannot_move_forward.
const isFutureDay = (day) => day && moment(day).startOf('day').isAfter(moment().startOf('day'));

const UpdateAssignmentModal = ({ onClose, updateAssignment, management }) => {
  const { t } = useContext(ConfigContext);

  const initialValues = useMemo(
    () => ({
      employmentRate: management?.employmentRate,
      startDate: management?.startDate || undefined
    }),
    [management]
  );

  const onSubmit = useCallback(
    async (values, form) => {
      const { dirtyFields } = form.getState();
      const dirtyValues = getDirtyFields(values, dirtyFields);
      if (!dirtyValues) {
        onClose();
        return;
      }

      const payload = { assignmentId: management.employeeContractV2Id };
      if (dirtyValues.employmentRate !== undefined) {
        const next = dirtyValues.employmentRate === '' ? undefined : Number(dirtyValues.employmentRate);
        if (next !== undefined) payload.employmentRate = next;
      }
      if (dirtyValues.startDate !== undefined && dirtyValues.startDate !== '') {
        payload.startDate = formatDate(dirtyValues.startDate);
      }

      try {
        await updateAssignment(payload);
        onClose();
      } catch {
        // useOptimisticMutation already shows the toaster.
      }
    },
    [updateAssignment, management, onClose]
  );

  return (
    <FormModal
      onSubmit={onSubmit}
      initialValues={initialValues}
      size="small"
      onClose={onClose}
      title={t('updateLeaveAssignment')}
      confirmText={t('update')}
      showCancelButton
      keepDirtyOnReinitialize>
      <Container>
        <InputField
          name="employmentRate"
          title={`${t('employmentRate')} (0-100)`}
          type="number"
          validate={(value) => isValueBetweenNumberRange({ value, min: 0, max: 100, isRequired: false })}
        />
        <DateField name="startDate" title={t('startDate')} usePortal isOutsideRange={isFutureDay} />
      </Container>
    </FormModal>
  );
};

export default UpdateAssignmentModal;
